'use client';
import type { FC, ReactNode } from 'react';

import { ClickableDiv } from '@wwe-portal/ui/components/button/clickable-div';
import { IcoMoonIcon } from '@wwe-portal/ui/components/icons/icomoon';
import { cn } from '@wwe-portal/ui/lib/lib';

export interface RowProps {
  rank?: string;
  player?: string;
  faction?: string;
  points?: string;
}

export interface LeaderboardRowProps<
  T extends { points: number; rank: number },
> {
  className?: string;
  data: T;
  title: ReactNode;
  endDate?: ReactNode;
  subtitle?: ReactNode;
  rowClassNames?: RowProps;
  isCurrentUser?: boolean;

  // Default is true
  compact?: boolean;
}

const getIconByRank = (rank: number) => {
  if (rank < 100) return 'diamond';
  if (rank < 1000) return 'more-99';
  return 'more-999';
};

export const RankCell: FC<{
  rank: number | string;
  highlight?: boolean;
  isCurrentUser?: boolean;
}> = ({ rank, highlight, isCurrentUser }) => {
  const rankNumber = Number(rank);
  return (
    <div
      className={cn('relative top-0 flex h-9 w-10 justify-center', {
        'text-text800': !isCurrentUser && rankNumber > 3,
        'text-yellow600': rankNumber === 1,
        'text-blue700': rankNumber === 2,
        'text-green700': rankNumber === 3,
        'text-yellow800': highlight,
        'text-lightSteelBlue': isCurrentUser && rankNumber > 3,
      })}
    >
      <IcoMoonIcon className="size-full" iconName={getIconByRank(rankNumber)} />
      {rank === 1 && (
        <div className="absolute inset-0 bottom-1 flex items-center justify-center">
          <div
            className="size-6 animate-pulse rounded-full"
            style={{
              background:
                'radial-gradient(rgba(255, 222, 0, .412), rgba(255, 222, 0, .412) 4%, hsla(0, 0%, 100%, 0) 70%)',
            }}
          />
        </div>
      )}
      <div
        className={cn(
          'absolute inset-0 bottom-1 top-[6px] flex items-center justify-center',
          {
            'top-0': rankNumber < 100,
          }
        )}
      >
        <span
          className={cn('text-xs font-semibold', {
            'text-text600': rankNumber > 3 && !highlight,
            'text-yellow800': highlight,
          })}
        >
          {rank}
        </span>
      </div>
    </div>
  );
};

export const PlayerCell: FC<{
  avatar: ReactNode;
  name: ReactNode;
  faction?: ReactNode;
}> = ({ name, avatar, faction }) => {
  return (
    <div className="flex w-full items-center gap-3 text-xs">
      {avatar && (
        <div className="relative size-[26px] overflow-hidden rounded-full">
          {avatar}
          <div className="border-blue600 absolute inset-0 rounded-full border border-solid" />
        </div>
      )}
      <div className="flex w-0 flex-1 flex-col justify-center">
        {name}
        {faction}
      </div>
    </div>
  );
};

export const CollapsableContent: FC<{
  points?: number;
  faction?: string;
  endDate?: ReactNode;
  expanded: boolean;
  customColumnNames?: Partial<{
    point: string;
  }>;
}> = ({ points, faction, expanded, endDate, customColumnNames }) => {
  return (
    <div
      className={cn(
        'max-h-0 overflow-hidden px-5 transition-[max-height] duration-500 ease-in-out data-[expanded=true]:max-h-screen'
      )}
      data-expanded={expanded}
    >
      <div className="border-back200 grid grid-cols-12 border-t pb-4 pt-5">
        {faction || endDate ? (
          <div className="col-span-7">
            <div className="text-text700 text-2xs font-semibold">
              {faction ? 'FACTION' : 'END DATE'}
            </div>
            <div className="text-text500 text-2xs leading-5">
              {faction ?? endDate}
            </div>
          </div>
        ) : null}
        {points != null && points > 0 && (
          <div
            className={cn('col-span-5 flex flex-col', {
              'items-end': faction != null,
            })}
          >
            <div className="text-text700 text-2xs font-semibold uppercase">
              {customColumnNames?.point || 'POINTS'}
            </div>
            <div className="text-text500 text-2xs leading-5">
              {Math.floor(points).toLocaleString()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const LeaderboardRowMobile = <
  T extends { points: number; rank: number; isCurrentUser?: boolean },
>({
  data,
  className,
  title,
  isCurrentUser = false,
}: LeaderboardRowProps<T>) => {
  return (
    <ClickableDiv
      className={cn('flex w-0 flex-1 flex-col pl-3 md:hidden', className)}
    >
      <div className="flex items-center justify-between py-2.5">
        <ClickableDiv className="flex w-full items-center gap-3">
          <RankCell rank={data.rank} isCurrentUser={isCurrentUser} />
          <div className="w-0 flex-1">{title}</div>
        </ClickableDiv>
      </div>
    </ClickableDiv>
  );
};

interface LeaderboardRowDesktopLayoutProps {
  rank: ReactNode;
  title: ReactNode;
  subtitle: ReactNode;
  points: ReactNode;
  rowClassNames?: RowProps;
  className?: string;
  compact: boolean;
}

export const LeaderboardRowDesktopLayout: FC<
  LeaderboardRowDesktopLayoutProps
> = ({ compact, ...props }) => {
  const Layout = compact
    ? LeaderboardRowDesktopLayoutCompact
    : LeaderboardRowDesktopLayoutWide;
  return <Layout {...props} />;
};

export const FactionLeaderboardRowDesktopLayout: FC<
  Omit<LeaderboardRowDesktopLayoutProps, 'compact' | 'subtitle'>
> = ({ rank, title, points, rowClassNames = {}, className }) => {
  return (
    <div
      className={cn(
        'grid min-h-[52px] grid-cols-[50px_repeat(11,minmax(0,_1fr))] p-5',
        className
      )}
    >
      <div
        className={cn(
          '3xl:col-span-2 col-span-2 min-[1380px]:col-span-3 2xl:col-span-3',
          rowClassNames.rank
        )}
      >
        {rank}
      </div>
      <div
        className={cn(
          '3xl:col-span-7 col-span-7 min-[1380px]:col-span-6 2xl:col-span-6',
          rowClassNames.player
        )}
      >
        {title}
      </div>
      <div
        className={cn(
          '3xl:col-span-3 col-span-3 text-right min-[1380px]:col-span-3 2xl:col-span-3',
          rowClassNames.points
        )}
      >
        {points}
      </div>
    </div>
  );
};

export const LeaderboardRowDesktopLayoutCompact: FC<
  Omit<LeaderboardRowDesktopLayoutProps, 'compact'>
> = ({
  rank,
  title: player,
  points,
  subtitle: faction,
  rowClassNames = {},
  className,
}) => {
  return (
    <div
      className={cn(
        'grid min-h-[52px] grid-cols-[50px_repeat(11,minmax(0,_1fr))] p-5',
        className
      )}
    >
      <div
        className={cn(
          '3xl:col-span-2 col-span-2 min-[1380px]:col-span-3 2xl:col-span-3',
          rowClassNames.rank
        )}
      >
        {rank}
      </div>
      <div
        className={cn(
          '3xl:col-span-4 col-span-4 min-[1380px]:col-span-6 2xl:col-span-6',
          rowClassNames.player
        )}
      >
        {player}
      </div>
      <div
        className={cn(
          '3xl:block 3xl:col-span-3 col-span-3 min-[1380px]:hidden 2xl:hidden',
          rowClassNames.faction
        )}
      >
        <div className="text-xs no-underline hover:no-underline">{faction}</div>
      </div>
      <div
        className={cn(
          '3xl:col-span-3 col-span-3 text-right min-[1380px]:col-span-3 2xl:col-span-3',
          rowClassNames.points
        )}
      >
        {points}
      </div>
    </div>
  );
};

export const LeaderboardRowDesktopLayoutWide: FC<
  Omit<LeaderboardRowDesktopLayoutProps, 'compact'>
> = ({
  rank,
  title: player,
  points,
  subtitle: faction,
  rowClassNames = {},
  className,
}) => {
  return (
    <div
      className={cn(
        'grid min-h-[52px] grid-cols-[70px_repeat(11,minmax(0,_1fr))] p-5',
        className
      )}
    >
      <div className={cn('3xl:col-span-2 col-span-2', rowClassNames.rank)}>
        {rank}
      </div>
      <div className={cn('3xl:col-span-4 col-span-4', rowClassNames.player)}>
        {player}
      </div>
      <div className={cn('3xl:col-span-3 col-span-3', rowClassNames.faction)}>
        <div className="text-xs no-underline hover:no-underline">{faction}</div>
      </div>
      <div
        className={cn(
          '3xl:col-span-3 col-span-3 text-right',
          rowClassNames.points
        )}
      >
        {points}
      </div>
    </div>
  );
};

export const LeaderboardRowDesktop = <
  T extends { points: number; rank: number },
>({
  className,
  data,
  title,
  subtitle,
  rowClassNames,
  isCurrentUser,
  compact = false,
}: LeaderboardRowProps<T>) => {
  return (
    <LeaderboardRowDesktopLayout
      compact={compact}
      rowClassNames={rowClassNames}
      className={cn(
        'hidden items-center md:grid',
        {
          'border-back200 rounded-xl border border-solid':
            data.rank === 1 && !isCurrentUser,
          'border-borderColor15 bg-borderColor7 rounded-xl border border-solid':
            isCurrentUser,
        },
        className
      )}
      points={
        <span className="text-text300 text-xs">
          {Math.floor(data.points).toLocaleString()}
        </span>
      }
      rank={<RankCell rank={data.rank} isCurrentUser={isCurrentUser} />}
      subtitle={subtitle}
      title={
        <div className="text-text300 ml-[-38px] flex h-full w-full items-center">
          {title}
        </div>
      }
    />
  );
};

export const FactionLeaderboardRowDesktop = <
  T extends { points: number; rank: number },
>({
  className,
  data,
  title,
  rowClassNames,
}: Omit<LeaderboardRowProps<T>, 'subtitle'>) => {
  return (
    <FactionLeaderboardRowDesktopLayout
      rowClassNames={rowClassNames}
      className={cn('hidden items-center md:grid', className)}
      points={
        <span className="text-text300 text-xs">
          {Math.floor(data.points).toLocaleString()}
        </span>
      }
      rank={<RankCell rank={data.rank} />}
      title={
        <div className="text-text300 ml-[-38px] flex h-full w-full items-center">
          {title}
        </div>
      }
    />
  );
};
